import {
  ActionCreatorWithPayload,
  ActionCreatorWithoutPayload,
  ActionWithPayload,
  actionCreator,
  isValidRxBeachAction,
} from '@ardoq/rxbeach';

/**
 * Request opening a bridge to ardoq-front.
 */
export const requestBridge = actionCreator<{ portalConfigId: string }>(
  '[bridge] REQUEST_BRIDGE'
);

/**
 * Tell other side of bridge to start relay
 */
export const startRelay = actionCreator<{ portalConfigId: string }>(
  '[bridge] START_RELAY'
);

/**
 * Notify current side or other side of bridge that the bridge has closed.
 * Note: Payload=undefined to avoid making a ts-ignore due to rxbeach typings.
 */
export const bridgeClosed = actionCreator<BridgeClosedPayload>(
  '[bridge] BRIDGE_CLOSED'
);

export type BridgeClosedPayload = {
  id: string;
  documentType: 'iframe' | 'popout' | 'main-window';
};

/**
 * Notify current side that bridge is open.
 * Prefer using the helper function on the portal:
 * ```
 * const portal = new SourcePortal(config);
 * await portal.opened()
 * ```
 */
export const bridgeOpened = actionCreator('[bridge] BRIDGE_OPENED');

// ============================================================================
// Action helpers
/**
 * Assert that an action creator is of a specific type, and extract its payload
 * type
 *
 * TODO: rxbeach's version of this does not work correctly because of the
 * difference between with/without payload.
 */
export function isActionOfType<T = undefined>(
  creatorFn: ActionCreatorWithPayload<T> | ActionCreatorWithoutPayload,
  action: ActionWithPayload<unknown>
): action is ActionWithPayload<T> {
  const hasPayloadKey = 'payload' in action;
  const hasMetaKey = 'meta' in action;

  return action.type === creatorFn.type && hasPayloadKey && hasMetaKey;
}

const hasValidOrigin = (
  object: { origin?: string } | undefined,
  expectedOrigin: string
) => {
  if (!object || !object.origin) return false;
  return object.origin === expectedOrigin;
};

/**
 * Parse bridge event, ensuring its origin is valid and the action exists
 */
export function parseActionBridgeActionFromEvent(
  event: MessageEvent<any>,
  expectedOrigin: string
) {
  // MessageEvents have to have a reference to the window that sent it.
  if (!event?.source) {
    return;
  }

  // Validate that the event originates from the same "origin"
  // eg. https://ardoq.com or https://subdomain.ardoq.com
  if (!hasValidOrigin(event, expectedOrigin)) {
    return;
  }

  const action = event.data;
  if (!isValidRxBeachAction(action)) {
    return;
  }

  return action;
}
